import Cookies from "js-cookie";
import { send } from "@utils/fetch";
import store from "@utils/redux/store";
import { setSurveyNameSpace } from "./surveyNameSpace";
import { addSurveyAnswers } from "./surveyAnswers";

/**
 * Action types
 */
export const UPDATE_USER_DATA = "userData/updateUserData";

/**
 * Action
 * Updates the store with new userdata
 * @param {Object} userData
 */
export function updateUserData(userData) {
  return {
    type: UPDATE_USER_DATA,
    userData,
  };
}

/**
 * Helper function to create a UTC date timestamp.
 */
function nowUTC() {
  const date = new Date();
  return date.getTime() + date.getTimezoneOffset() * 60 * 1000;
}

/**
 * The Django API uses the `_userId` cookie for some purposes. This utility helps
 * us set the cookie whenever its updated.
 * @param {*} userId
 */
function setUserIdCookie(userId) {
  if (!userId) return;
  const { serverContext } = store.getState();
  const cookiedUserId = Cookies.get("_userId");
  if (cookiedUserId !== userId) {
    Cookies.set("_userId", userId, {
      domain: serverContext.session_cookie_domain,
    });
  }
}

/**
 * Thunk
 * @param {string} userId
 * @param {object} serverContext
 */
export function fetchAndInitializeExistingUser(userId, serverContext) {
  return dispatch => {
    console.log(`loading => users/${userId}`);
    return send("POST", `/userdata/api/v3/user/data/`, { clientUserId: userId })
      .then(json => {
        // Make sure the user has a userId field set in their userData.
        const userData = { ...json.data };
        if (!userData.user_id) userData.user_id = userId;
        if (!userData.mixpanel_distinct_id)
          userData.mixpanel_distinct_id = userId;

        dispatch(updateUserData(userData));
        setUserIdCookie(userData.user_id);
        let nameSpace =
          json.data?.surveyNameSpace ||
          (serverContext.country_code === "US"
            ? "personaSurveyUS"
            : "personaSurveyNonUS");
        if (nameSpace) {
          dispatch(setSurveyNameSpace(nameSpace));
          dispatch(addSurveyAnswers(json.data[nameSpace] || {}));
        }
      })
      .catch(e => {
        window.SentryHelper.captureException(e, "UserDataLoad-Failed to fetch");
      });
  };
}

/**
 * Thunk
 * @param {Object} serverContext
 */
export function initializeNewUser(serverContext) {
  return dispatch => {
    const data = {
      mixpanel_distinct_id: serverContext.distinct_id,
      user_id: serverContext.user_id,
    };
    dispatch(updateUserData(data));

    setUserIdCookie(serverContext.user_id);

    let nameSpace =
      data?.surveyNameSpace || serverContext.country_code === "US"
        ? "personaSurveyUS"
        : "personaSurveyNonUS";
    if (nameSpace) dispatch(setSurveyNameSpace(nameSpace));
    return Promise.resolve(data);
  };
}

/**
 * Thunk
 * Generic save user data call, updates entire userdata object.
 * @param {Object} properties
 */
export function setUserProperties(properties) {
  return (dispatch, getState) => {
    return submitUserDataUpdate(dispatch, getState, "save", properties);
  };
}

/**
 * Thunk
 * Updates the main survey answers.
 * @param {Object} properties
 */
export function saveMainSurveyAnswers(properties) {
  return (dispatch, getState) => {
    return submitUserDataUpdate(
      dispatch,
      getState,
      "saveMainSurvey",
      properties
    );
  };
}

/**
 * Thunk
 * Updates the payment survey answers.
 * @param {Object} properties
 */
export function savePurchaseSurveyAnswers(properties) {
  return (dispatch, getState) => {
    return submitUserDataUpdate(
      dispatch,
      getState,
      "savePurchaseSurvey",
      properties
    );
  };
}

/**
 * Thunk
 *
 * @param {Object} properties
 */
export function saveProfile(properties) {
  return (dispatch, getState) => {
    return submitUserDataUpdate(dispatch, getState, "saveProfile", properties);
  };
}

/**
 * Thunk
 *
 * @param {Object} properties
 */
export function saveCareersNewsletter(properties) {
  return (dispatch, getState) => {
    return submitUserDataUpdate(
      dispatch,
      getState,
      "saveCareersNewsletter",
      properties
    );
  };
}

/**
 * Thunk
 * Saves the experiment the user is in.
 * @param {Object} properties
 */
export function saveExperiment(properties) {
  return (dispatch, getState) => {
    return submitUserDataUpdate(dispatch, getState, "experiment", properties);
  };
}

/**
 * Utility function used by the thunks to execute the api call. It requires
 * the dispatch function generated by the thunks to update the store and state
 * when the updated userData is received from the server.
 * @param {Redux Dispatch} dispatch
 * @param {String} saveType
 * @param {Object} properties
 */
function submitUserDataUpdate(dispatch, getState, saveType, properties) {
  const { userData } = getState();
  const payload = properties;
  payload.lastUpdated = nowUTC();
  payload.clientUserId = serverContext.user_id;
  console.log(`saving => users/${userData.user_id}`, payload);
  return send("POST", `/userdata/api/v3/user/data/${saveType}/`, payload)
    .then(json => {
      const userData = { ...json.data };
      if (json?.user_id) userData.user_id = json.user_id;
      if (json?.distinct_id) userData.mixpanel_distinct_id = json.distinct_id;
      dispatch(updateUserData(userData));
      setUserIdCookie(json?.user_id || userData.user_id);

      console.log("Got user_id from server", json.user_id);
      console.log("Got distinct_id from server", json.mixpanel_distinct_id);
    })
    .catch(e => {
      window.SentryHelper.captureException(e, "UserDataSave-Failed to fetch");
    });
}

function userDataReducer(state = {}, action) {
  switch (action.type) {
    case UPDATE_USER_DATA:
      return Object.assign({}, state, action.userData);
    default:
      return state;
  }
}

export default userDataReducer;
